import { forwardRef, type ReactElement } from "react";
import {
  Button,
  Dropdown,
  Input,
  Menu,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Option,
  SearchBox,
  Textarea,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
  type ButtonProps,
  type DropdownProps,
  type InputProps,
  type MenuCheckedValueChangeData,
  type SearchBoxProps,
  type TextareaProps,
} from "@fluentui/react-components";
import { ChevronDown12Regular } from "@fluentui/react-icons";
import { useDialogLayer } from "@/components/ui/dialog-layer";

const useStyles = makeStyles({
  /** Align Fluent small controls to PRD 28px control height. */
  control: {
    minHeight: "28px",
    height: "28px",
    maxHeight: "28px",
    width: "100%",
    /* Fluent Dropdown defaults to minWidth 250px; let it shrink to its grid/flex cell */
    minWidth: 0,
    fontSize: tokens.fontSizeBase200,
    "& .fui-Dropdown__button": {
      fontSize: tokens.fontSizeBase200,
      lineHeight: "1",
      height: "28px",
      minHeight: "28px",
      maxHeight: "28px",
      paddingTop: 0,
      paddingBottom: 0,
      alignItems: "center",
    },
    "& .fui-Dropdown__expandIcon": {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      alignSelf: "center",
      lineHeight: 0,
      height: "auto",
    },
  },
  controlDialog: {
    minHeight: "24px",
    height: "24px",
    maxHeight: "24px",
    width: "100%",
    minWidth: 0,
    fontSize: tokens.fontSizeBase200,
    "& .fui-Dropdown__button": {
      fontSize: tokens.fontSizeBase200,
      lineHeight: "1",
      height: "24px",
      minHeight: "24px",
      maxHeight: "24px",
      paddingTop: 0,
      paddingBottom: 0,
      alignItems: "center",
    },
    "& .fui-Dropdown__expandIcon": {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      alignSelf: "center",
      lineHeight: 0,
      height: "auto",
    },
  },
  textarea: {
    width: "100%",
    "& textarea": {
      lineHeight: tokens.lineHeightBase300,
    },
  },
  dropdownList: {
    maxHeight: "280px",
    padding: "4px",
  },
  dropdownListDialog: {
    maxHeight: "240px",
    padding: "3px",
  },
  /** 모달은 body에 pointer-events:none을 건다. 목록은 대화상자 레이어로 올리고 클릭을 다시 받는다. */
  dropdownListLayer: {
    zIndex: 100,
    pointerEvents: "auto",
  },
  option: {
    minHeight: "26px",
    fontSize: tokens.fontSizeBase200,
    lineHeight: "20px",
    paddingTop: "3px",
    paddingBottom: "3px",
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    whiteSpace: "nowrap",
    "& .fui-Option__checkIcon": {
      fontSize: "13px",
    },
  },
  optionDialog: {
    minHeight: "24px",
    fontSize: tokens.fontSizeBase200,
    lineHeight: "18px",
    paddingTop: "2px",
    paddingBottom: "2px",
    paddingLeft: tokens.spacingHorizontalSNudge,
    paddingRight: tokens.spacingHorizontalSNudge,
    "& .fui-Option__checkIcon": {
      fontSize: "12px",
    },
  },
  optionWithDescription: {
    alignItems: "center",
    whiteSpace: "nowrap",
  },
  optionDescription: {
    display: "inline",
    fontSize: tokens.fontSizeBase100,
    lineHeight: "16px",
    color: tokens.colorNeutralForeground3,
    marginLeft: tokens.spacingHorizontalS,
    whiteSpace: "nowrap",
  },
  /** 페이지·다이얼로그 버튼 라벨. Fluent small 리셋(14px·600)이 이기지 않게 12px Regular로 고정. */
  buttonLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    lineHeight: tokens.lineHeightBase200,
    whiteSpace: "nowrap",
  },
  /** 삭제처럼 되돌리기 어려운 동작. 글자와 아이콘만 위험색이고, 배경은 subtle 그대로다. */
  danger: {
    "&&&": { color: tokens.colorPaletteRedForeground1 },
    "&&&:hover": { color: tokens.colorPaletteRedForeground1 },
    "&&&:hover:active": { color: tokens.colorPaletteRedForeground1 },
    "&&&:disabled": { color: tokens.colorPaletteRedForeground1 },
  },
  page: {
    minHeight: "28px",
    height: "28px",
    maxHeight: "28px",
  },
  pageIcon: {
    minHeight: "28px",
    height: "28px",
    maxHeight: "28px",
    minWidth: "28px",
    width: "28px",
    maxWidth: "28px",
    padding: 0,
  },
});

/** PRD §8.6: 28px page CTA, 24px dialog action. */
export type FuiControlDensity = "page" | "dialog";
export type FuiButtonDensity = FuiControlDensity;

export const FuiButton = forwardRef<HTMLButtonElement, ButtonProps & { density?: FuiButtonDensity; tone?: "danger" }>(function FuiButton(
  {
    density = "page",
    tone,
    className,
    children,
    size: _size,
    ...props
  },
  ref,
) {
  const styles = useStyles();
  const iconOnly = children == null || children === false || children === "";
  const classes = mergeClasses(styles.buttonLabel, tone === "danger" && styles.danger, density === "page" && (iconOnly ? styles.pageIcon : styles.page), className);
  return (
    <Button ref={ref} size="small" className={classes} {...props}>
      {children}
    </Button>
  );
});

/** Fluent Input sized to the app’s 28px control density (or 24px in dialog). */
export const FuiInput = forwardRef<HTMLInputElement, InputProps & { density?: FuiControlDensity }>(function FuiInput(
  {
    density = "page",
    className,
    ...props
  },
  ref,
) {
  const styles = useStyles();
  if (density === "dialog") {
    return <Input ref={ref} size="small" className={className} {...props} />;
  }
  return <Input ref={ref} size="small" className={mergeClasses(styles.control, className)} {...props} />;
});

/** Fluent Textarea at the same 12px type as FuiInput; grows vertically only. */
export const FuiTextarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function FuiTextarea({ className, resize = "vertical", ...props }, ref) {
  const styles = useStyles();
  return <Textarea ref={ref} size="small" resize={resize} className={mergeClasses(styles.textarea, className)} {...props} />;
});

/** Fluent SearchBox sized to the app’s 28px control density (or 24px in dialog). */
export function FuiSearchBox({
  density = "page",
  className,
  ...props
}: SearchBoxProps & { density?: FuiControlDensity }) {
  const styles = useStyles();
  if (density === "dialog") {
    return <SearchBox size="small" className={className} {...props} />;
  }
  return <SearchBox size="small" className={mergeClasses(styles.control, className)} {...props} />;
}

export type FuiOption = { value: string; label: string; description?: string };

/** Fluent Dropdown sized to the app’s 28px control density (or 24px in dialog). Supports optional `prefix` for button label. */
export function FuiDropdown({
  density = "page",
  value,
  options,
  onValueChange,
  placeholder,
  className,
  prefix,
  mountNode: mountNodeProp,
  positioning,
  ...rest
}: Omit<DropdownProps, "children" | "value" | "selectedOptions" | "onOptionSelect"> & {
  density?: FuiControlDensity;
  value: string;
  options: FuiOption[];
  onValueChange: (value: string) => void;
  placeholder?: string;
  prefix?: string;
}) {
  const styles = useStyles();
  const dialogLayer = useDialogLayer();
  const mountNode = mountNodeProp ?? dialogLayer;
  const descriptive = options.some((o) => o.description);
  const selected = options.find((o) => o.value === value);
  const selectedLabel = selected?.label ?? (value ? value : "");
  const displayValue = selected
    ? prefix
      ? `${prefix}: ${selected.label}`
      : selected.label
    : (placeholder ?? "");

  return (
    <Dropdown
      size="small"
      className={mergeClasses(
        density === "dialog" ? styles.controlDialog : styles.control,
        className
      )}
      placeholder={placeholder}
      value={displayValue}
      selectedOptions={value ? [value] : []}
      onOptionSelect={(_, data) => {
        if (data.optionValue != null) onValueChange(data.optionValue);
      }}
      mountNode={mountNode}
      positioning={{
        // Fluent 기본은 팝오버 너비를 트리거에 고정한다(matchTargetSize).
        // 여기서는 그 너비를 최소값으로만 쓰고, 옵션이 더 길면 내용만큼 넓힌다.
        // autoSize를 height로 두면 세로만 화면에 맞추고, 너비 고정(resetMaxSize)은 하지 않는다.
        autoSize: "height",
        matchTargetSize: "width",
        ...(mountNode ? { strategy: "fixed" as const, position: "below" as const, align: "start" as const } : {}),
        ...(positioning && typeof positioning === "object" ? positioning : {}),
      }}
      listbox={{
        className: mergeClasses(
          styles.dropdownList,
          density === "dialog" && styles.dropdownListDialog,
          mountNode && styles.dropdownListLayer,
        ),
        // 인라인 width가 있어야 matchTargetSize가 너비를 트리거에 고정하지 않는다.
        // 같은 미들웨어가 --fui-match-target-size에 트리거 너비를 넣는다.
        style: {
          width: "max-content",
          minWidth: descriptive
            ? "max(272px, var(--fui-match-target-size, 0px))"
            : "var(--fui-match-target-size, 0px)",
        },
      }}
      {...rest}
    >
      {options.map((o) => (
        <Option
          key={o.value}
          value={o.value}
          text={o.description ? `${o.label} ${o.description}` : o.label}
          className={mergeClasses(
            styles.option,
            density === "dialog" && styles.optionDialog,
            o.description && styles.optionWithDescription
          )}
        >
          {o.description ? (
            <>
              {o.label}
              <span className={styles.optionDescription}>{o.description}</span>
            </>
          ) : (
            o.label
          )}
        </Option>
      ))}
    </Dropdown>
  );
}

const useFilterSelectStyles = makeStyles({
  trigger: {
    height: "28px",
    minHeight: "28px",
    maxHeight: "28px",
    padding: "0 8px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "6px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke1),
    cursor: "pointer",
    fontSize: tokens.fontSizeBase200,
    lineHeight: "28px",
    boxSizing: "border-box",
    userSelect: "none",
    transitionProperty: "background-color, border-color, box-shadow",
    transitionDuration: tokens.durationFast,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      ...shorthands.borderColor(tokens.colorNeutralStroke1Hover),
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  triggerDialog: {
    height: "24px",
    minHeight: "24px",
    maxHeight: "24px",
    lineHeight: "24px",
    padding: "0 6px",
    borderRadius: tokens.borderRadiusSmall,
    fontSize: tokens.fontSizeBase100,
  },
  triggerDisabled: {
    cursor: "not-allowed",
    opacity: 0.6,
    backgroundColor: tokens.colorNeutralBackgroundDisabled,
    ...shorthands.borderColor(tokens.colorNeutralStrokeDisabled),
    ":hover": {
      backgroundColor: tokens.colorNeutralBackgroundDisabled,
      ...shorthands.borderColor(tokens.colorNeutralStrokeDisabled),
    },
  },
  contentGroup: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    minWidth: 0,
    overflow: "hidden",
    flex: 1,
  },
  label: {
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
    fontSize: "12px",
    flexShrink: 0,
    lineHeight: 1,
  },
  divider: {
    width: "1px",
    height: "10px",
    backgroundColor: tokens.colorNeutralStroke2,
    flexShrink: 0,
  },
  value: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightRegular,
    fontSize: "12px",
    lineHeight: 1,
    minWidth: 0,
    overflow: "hidden",
  },
  valueIcon: {
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
  },
  valueText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  chevron: {
    color: tokens.colorNeutralForeground3,
    fontSize: "12px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginLeft: "auto",
    paddingLeft: "4px",
  },
  menuList: {
    maxHeight: "280px",
    width: "100%",
  },
  menuItem: {
    whiteSpace: "nowrap",
  },
  popover: {
    zIndex: 100,
    pointerEvents: "auto",
  },
});

export interface FuiFilterSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  /** 값 옆 표식. 트리거와 목록이 같은 아이콘을 쓴다. */
  icon?: ReactElement;
}

export interface FuiFilterSelectProps {
  label: string;
  value: string;
  options: FuiFilterSelectOption[];
  onValueChange: (value: string) => void;
  density?: FuiControlDensity;
  ariaLabel?: string;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
}

/**
 * 툴바 및 필터 바 전용 세련된 셀렉트 컴포넌트.
 * 버튼 내 라벨(은은한 보조색)과 선택값(강조색)의 시각적 위계를 분리하고,
 * 팝오버 메뉴에서는 접두사 없이 깔끔한 값 목록을 라디오 체크 표시와 함께 제공합니다.
 */
export function FuiFilterSelect({
  label,
  value,
  options,
  onValueChange,
  density = "page",
  ariaLabel,
  className,
  style,
  disabled = false,
  mountNode,
}: FuiFilterSelectProps & { mountNode?: HTMLElement }) {
  const styles = useFilterSelectStyles();
  const selectedOption = options.find((o) => o.value === value);
  const displayValue = selectedOption?.label ?? value;

  const handleCheckedChange = (_: unknown, data: MenuCheckedValueChangeData) => {
    const nextVal = data.checkedItems[0];
    if (nextVal != null && nextVal !== value) {
      onValueChange(nextVal);
    }
  };

  return (
    <Menu
      checkedValues={{ filter: [value] }}
      onCheckedValueChange={handleCheckedChange}
      mountNode={mountNode}
      positioning={{
        // FuiDropdown과 같다. 팝오버는 트리거보다 좁아지지 않고, 옵션이 더 길면 내용만큼 넓어진다.
        autoSize: "height",
        matchTargetSize: "width",
        ...(mountNode ? { strategy: "fixed" as const, position: "below" as const, align: "start" as const } : {}),
      }}
    >
      <MenuTrigger disableButtonEnhancement>
        <button
          type="button"
          role="combobox"
          aria-expanded={false}
          aria-label={ariaLabel ?? `${label}: ${displayValue}`}
          disabled={disabled}
          className={mergeClasses(
            styles.trigger,
            density === "dialog" && styles.triggerDialog,
            disabled && styles.triggerDisabled,
            className
          )}
          style={style}
        >
          <span className={styles.contentGroup}>
            <span className={styles.label}>{label}</span>
            <span className={styles.divider} aria-hidden="true" />
            <span className={styles.value}>
              {selectedOption?.icon ? (
                <span className={styles.valueIcon} aria-hidden>
                  {selectedOption.icon}
                </span>
              ) : null}
              <span className={styles.valueText}>{displayValue}</span>
            </span>
          </span>
          <span className={styles.chevron} aria-hidden="true">
            <ChevronDown12Regular />
          </span>
        </button>
      </MenuTrigger>
      <MenuPopover
        className={styles.popover}
        style={{
          // 인라인 width가 있어야 matchTargetSize가 너비를 트리거에 고정하지 않는다.
          width: "max-content",
          minWidth: "var(--fui-match-target-size, 0px)",
          maxWidth: "none",
        }}
      >
        <MenuList className={styles.menuList}>
          {options.map((opt) => (
            <MenuItemRadio
              key={opt.value}
              name="filter"
              value={opt.value}
              disabled={opt.disabled}
              icon={opt.icon}
              className={styles.menuItem}
            >
              {opt.label}
            </MenuItemRadio>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}
